/**
 * OutreachActivityForm
 *
 * Anonymous, aggregate-only outreach activity entry for CBOs running
 * community outreach (Somos Mayfair / FUERTES first). Renders the
 * "Somos — Outreach Activity" form_template (or whichever
 * form_type='activity_log' template the current CBO has active),
 * honors conditional visibility from com_fields.visibility_rules,
 * and submits via the PII-guarded POST /api/outreach-activities/
 * endpoint.
 *
 * Why a bespoke renderer instead of FormInstanceEditor:
 *   FormInstanceEditor is tied to the standard form_instances workflow
 *   (creates an instance via the regular flow, supports billing capture,
 *   signatures, hard-copy tracking, etc.) and bypasses the PII guard
 *   we added in app/routers/outreach_activities.py. This component
 *   reuses RepeatableGroupRenderer for the household_families group
 *   and includes its own minimal field renderer — sized to exactly the
 *   field types the Somos templates use.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext, Controller, useWatch } from 'react-hook-form';
import { Save, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formsAPI, FormTemplate, FormFieldSchema, FormSectionSchema } from '@/services/api/forms';
import { RepeatableGroupRenderer } from '@/components/forms/RepeatableGroupRenderer';
import { normalizeOptions } from '@/components/forms/fieldOptions';
import { isStaffBound, isStaffMultiSelect, useOrgStaffOptions } from '@/components/forms/staffOptions';
import { StaffMultiSelect, toStringArray } from '@/components/forms/StaffMultiSelect';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  createOutreachActivity,
  OutreachActivity,
} from '@/services/api/outreachActivities';

interface OutreachActivityFormProps {
  /** Override which template to load. Defaults to the first active
   *  form_type='activity_log' template in the current CBO. */
  templateId?: number;
  /** Field id where the persistent PII disclosure should appear under the
   *  textarea. Matches the Somos seed's notes-and-follow-ups field. */
  piiDisclosureFieldId?: string;
  onSubmitted?: (activity: OutreachActivity) => void;
  onCancel?: () => void;
}

const PII_DISCLOSURE_COPY =
  'Do not enter participant names, phone numbers, emails, or other identifying information. ' +
  'Use your external promotora documentation for any individual follow-up details. ' +
  'This field is for aggregate descriptions only (e.g., "3 families interested in legal services follow-up").';

export const OutreachActivityForm: React.FC<OutreachActivityFormProps> = ({
  templateId,
  piiDisclosureFieldId = 'outreach_notes_followups',
  onSubmitted,
  onCancel,
}) => {
  const methods = useForm<Record<string, any>>({ defaultValues: {}, shouldUnregister: false });
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [piiFieldHit, setPiiFieldHit] = useState<{ field: string; category: string } | null>(null);
  const [successId, setSuccessId] = useState<number | null>(null);
  const { t } = useAppTranslation();

  // Load the right template. If templateId is omitted, fetch active
  // activity_log templates and use the first one (Somos has exactly one).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (templateId) {
          const t = await formsAPI.getFormTemplate(templateId);
          if (!cancelled) setTemplate(t);
        } else {
          const templates = await formsAPI.listFormTemplates({
            form_type: 'activity_log' as any,
            is_active: true,
          });
          const first = templates?.[0];
          if (!first) {
            setLoadError(
              t(
                'outreach.no_template',
                'No active outreach-activity template found for this organization. ' +
                  'Ask an administrator to create one in the Form Library.',
              ),
            );
            return;
          }
          if (!cancelled) setTemplate(first);
        }
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message || t('outreach.template_load_failed', 'Failed to load form template.'));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const onSubmit = async (action: 'draft' | 'submitted') => {
    if (!template) return;
    setSubmitting(true);
    setServerError(null);
    setPiiFieldHit(null);
    setSuccessId(null);

    const fieldData = methods.getValues();
    const notes = typeof fieldData[piiDisclosureFieldId] === 'string' ? fieldData[piiDisclosureFieldId] : '';

    const result = await createOutreachActivity({
      form_template_id: template.id,
      field_data: fieldData,
      status: action,
      notes: notes || undefined,
    });
    setSubmitting(false);

    if (result.ok) {
      setSuccessId(result.activity.id);
      onSubmitted?.(result.activity);
      return;
    }
    if (result.kind === 'pii') {
      setPiiFieldHit({ field: result.field, category: result.category });
      setServerError(result.message);
      return;
    }
    setServerError(result.message);
  };

  if (loadError) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-6 flex gap-3 items-start">
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-amber-900">
              {t('outreach.form_not_configured', 'Outreach activity form is not configured')}
            </div>
            <div className="text-sm text-amber-800 mt-1">{loadError}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!template) {
    return (
      <div className="text-sm text-gray-500 py-8">
        {t('outreach.loading_form', 'Loading outreach activity form…')}
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit('submitted');
        }}
        className="space-y-6 max-w-4xl mx-auto pb-12"
      >
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{template.name}</h1>
          {template.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{template.description}</p>
          )}
        </header>

        {successId && (
          <div className="flex gap-2 items-start text-sm bg-green-50 border border-green-200 text-green-900 rounded-md p-3">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
            <span>
              {t(
                'outreach.saved',
                'Activity #{{id}} saved. You can log another below or return to your activity list.',
                { id: successId },
              )}
            </span>
          </div>
        )}

        {serverError && (
          <div className="flex gap-2 items-start text-sm bg-red-50 border border-red-200 text-red-900 rounded-md p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
            <div>
              <div className="font-medium">{t('outreach.submission_blocked', 'Submission blocked')}</div>
              <div className="mt-1">{serverError}</div>
              {piiFieldHit && (
                <div className="mt-1 text-xs">
                  {t('outreach.pii_detected', 'Detected {{category}} in field', {
                    category: piiFieldHit.category,
                  })}{' '}
                  <code className="bg-red-100 px-1 rounded">{piiFieldHit.field}</code>.
                </div>
              )}
            </div>
          </div>
        )}

        {(template.sections || []).map((section) => (
          <Section
            key={section.section_id}
            section={section}
            piiDisclosureFieldId={piiDisclosureFieldId}
          />
        ))}

        <footer className="flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 py-3 -mx-4 px-4">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              {t('common.cancel', 'Cancel')}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onSubmit('draft')} disabled={submitting}>
            <Save className="w-4 h-4 mr-2" />
            {t('common.save_draft', 'Save draft')}
          </Button>
          <Button type="submit" disabled={submitting}>
            <Send className="w-4 h-4 mr-2" />
            {t('common.submit', 'Submit')}
          </Button>
        </footer>
      </form>
    </FormProvider>
  );
};

// ---------------------------------------------------------------------------
// Section + field rendering (RHF-aware)
// ---------------------------------------------------------------------------

interface SectionProps {
  section: FormSectionSchema;
  piiDisclosureFieldId: string;
}

const Section: React.FC<SectionProps> = ({ section, piiDisclosureFieldId }) => {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{section.title}</CardTitle>
        {section.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400">{section.description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {(section.fields || []).map((field) => (
          <FieldRow
            key={field.field_id}
            field={field}
            piiDisclosureFieldId={piiDisclosureFieldId}
          />
        ))}
      </CardContent>
    </Card>
  );
};

interface FieldRowProps {
  field: FormFieldSchema & { field_group_id?: number | null };
  piiDisclosureFieldId: string;
}

const FieldRow: React.FC<FieldRowProps> = ({ field, piiDisclosureFieldId }) => {
  const visible = useFieldVisibility(field);
  if (!visible) return null;

  return (
    <div>
      {field.field_type !== 'field_group' && (
        <Label htmlFor={field.field_id} className="text-sm font-medium">
          {field.label}
          {field.required && <span className="text-red-600 ml-1">*</span>}
        </Label>
      )}
      {field.description && field.field_id !== piiDisclosureFieldId && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-1">{field.description}</p>
      )}

      <FieldInput field={field} />

      {field.field_id === piiDisclosureFieldId && (
        <div className="mt-2 flex gap-2 items-start text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-700" />
          <span>{PII_DISCLOSURE_COPY}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Evaluate visibility_rules against the live form values. Mirrors
 * FormRenderer.tsx::isFieldVisible so the UX is consistent between the
 * preview surface and the live editor.
 */
function useFieldVisibility(field: FormFieldSchema): boolean {
  const rules = field.visibility_rules as
    | { logic?: 'AND' | 'OR'; conditions?: Array<{ field: string; operator: string; value?: any }> }
    | undefined;
  // Always watch — useWatch lets us depend on every value without specifying names
  const allValues = useWatch();
  if (!rules || !rules.conditions || rules.conditions.length === 0) return true;

  const results = rules.conditions.map((cond) => {
    const depRaw = allValues?.[cond.field];
    const strVal = depRaw == null ? '' : String(depRaw);
    switch (cond.operator) {
      case 'is_populated':
        return strVal !== '' && strVal !== 'false' && strVal !== 'null' && strVal !== 'undefined';
      case 'is_empty':
        return strVal === '' || strVal === 'null' || strVal === 'undefined';
      case 'equals':
        return strVal === String(cond.value ?? '');
      case 'not_equals':
        return strVal !== String(cond.value ?? '');
      case 'contains':
        return strVal.toLowerCase().includes(String(cond.value ?? '').toLowerCase());
      case 'in':
        return String(cond.value ?? '')
          .split(',')
          .map((s) => s.trim())
          .includes(strVal);
      default:
        return true;
    }
  });
  return rules.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
}

const FieldInput: React.FC<{ field: FormFieldSchema & { field_group_id?: number | null } }> = ({ field }) => {
  const { t } = useAppTranslation();
  const { register, control } = useFormContext();
  const id = field.field_id;
  const staticOptions = useMemo(() => normalizeOptions(field.options), [field.options]);
  const staffBound = isStaffBound(id, (field as any).options_source);
  const staffOptions = useOrgStaffOptions();
  // Staff-bound fields ALWAYS use the live staff list, even if a static
  // options list got serialized into the snapshot for some reason.
  const options = staffBound ? staffOptions : staticOptions;

  switch (field.field_type) {
    case 'field_group':
      return <RepeatableGroupRenderer field={field as any} />;
    case 'date':
      return <Input id={id} type="date" {...register(id)} />;
    case 'time':
      return <Input id={id} type="time" {...register(id)} />;
    case 'number':
      return (
        <Input
          id={id}
          type="number"
          min={field.validation?.min}
          max={field.validation?.max}
          step={field.validation?.step}
          {...register(id, { valueAsNumber: true })}
        />
      );
    case 'textarea':
      return <Textarea id={id} rows={3} {...register(id)} />;
    case 'select':
      return (
        <select
          id={id}
          {...register(id)}
          className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
        >
          <option value="">{staffBound && options.length === 0 ? 'Loading staff…' : '— Select —'}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case 'multiselect':
    case 'checkbox':
      return (
        <Controller
          control={control}
          name={id}
          defaultValue={field.field_type === 'multiselect' || staffBound ? [] : false}
          render={({ field: rhfField }) => {
            // Boolean checkbox (Yes/No) — bool com_field with no options and not staff-bound
            if (field.field_type === 'checkbox' && !staffBound && options.length === 0) {
              return (
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!rhfField.value}
                    onChange={(e) => rhfField.onChange(e.target.checked)}
                  />
                  {t('common.yes', 'Yes')}
                </label>
              );
            }
            // Picking PEOPLE gets the multi-select, not a checkbox row — the
            // staff list is unbounded, so checkboxes push the rest of the form
            // off screen. Same control the generic FormInstanceEditor uses, so
            // Staff Attendees looks identical wherever it is opened (TRUS-414).
            if (isStaffMultiSelect(id, field.field_type)) {
              return (
                <StaffMultiSelect
                  options={options}
                  value={toStringArray(rhfField.value)}
                  onChange={(next) => rhfField.onChange(next)}
                  placeholder={t('outreach.select_staff', 'Select staff…')}
                  emptyHint={t('outreach.no_staff', 'No staff found for this organization')}
                />
              );
            }
            // Multi-select with options — render as a list of checkboxes
            const selected = Array.isArray(rhfField.value) ? rhfField.value : [];
            const toggle = (val: string) => {
              if (selected.includes(val)) {
                rhfField.onChange(selected.filter((s: string) => s !== val));
              } else {
                rhfField.onChange([...selected, val]);
              }
            };
            if (staffBound && options.length === 0) {
              return <div className="text-sm text-gray-500">{t('outreach.loading_staff', 'Loading staff list…')}</div>;
            }
            return (
              <div className="flex flex-wrap gap-3">
                {options.map((opt) => (
                  <label key={opt.value} className="inline-flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => toggle(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            );
          }}
        />
      );
    case 'text':
    default: {
      // Trigger browser address autocomplete for any text field whose name
      // implies an address/location/place. Promotoras enter venue addresses
      // for free-text "where" fields and benefit from the OS-level history.
      const nameLower = (field.field_name || '').toLowerCase();
      const isAddressy =
        nameLower.includes('address') ||
        nameLower.includes('location') ||
        nameLower.includes('venue') ||
        nameLower.includes('place');
      return (
        <Input
          id={id}
          type="text"
          maxLength={field.validation?.max_length}
          autoComplete={isAddressy ? 'street-address' : undefined}
          {...register(id)}
        />
      );
    }
  }
};

export default OutreachActivityForm;
